import { BasketDrawer } from '@common/components/BasketDrawer/BasketDrawer';
import { calculateTotalNumberOfItemsInBasket } from '@common/components/BasketDrawer/BasketDrawer.helpers';
import { ModuleType } from '@common/components/BasketDrawer/BasketListing.types';
import { useModulesToRecommend } from '@common/components/BasketDrawer/hooks/useModulesToRecommend';
import { useRoomExtras } from '@features/rooms';
import { useTranslations } from '@lang';
import { FC, useEffect, useState } from 'react';
import { useRouter } from 'shared-routing';
import {
  CloseModalButton,
  FormWrapper,
  Header,
  InnerScroller,
  LeftSideBarWrapper,
  MobileHeading,
  Modal,
  PrevStepButton,
  RightSideBarWrapper,
  SplitBarWrapper,
  SubTitle,
  Title,
} from '@common/components/BookingForms';
import { Section } from '@common/components/BookingForms/Section';
import { Spinner } from '@common/components/Spinner';
import { StickyBarWrapper } from '@common/components/StickyBar';
import { Routes } from '@common/constants';
import { SignIn } from '../SignIn';
import { getFormSteps, RoomRateFormStepNames, STICKY_BAR_ID } from './FormSteps';
import {
  RoomRateConfigurationWizardDispatchProps,
  RoomRateConfigurationWizardProps,
  RoomRateConfigurationWizardStoreProps,
} from './RoomRateConfigurationWizard.types';
import { multiRoomRateBookingFormConfig, roomRateBookingFormConfig } from './shared';
import { some } from '@utils/some';
import { useGetSpaSettings } from '@features/spa';

const titleIds = {
  [RoomRateFormStepNames.GUESTS]: 'room.guestDetailsStepTitle',
  [RoomRateFormStepNames.EXTRAS]: 'room.extrasStepTitle',
};

const routesMap = {
  room: {
    booking: Routes.ROOM_BOOKING,
    detail: Routes.ROOM_DETAIL,
  },
  rate: {
    booking: Routes.RATE_BOOKING,
    detail: Routes.RATE_DETAIL,
  },
};

type RoomConfigProps = RoomRateConfigurationWizardStoreProps &
  RoomRateConfigurationWizardProps &
  RoomRateConfigurationWizardDispatchProps;

export const RoomRateConfigurationWizard: FC<RoomConfigProps> = ({
  destroy,
  bookingStep,
  property,
  basketDetails,
  isFetching,
  basketGuests,
  journeyParams,
  roomRateAddToBasket,
  multiRoomRateAddToBasket,
  language,
  metaEntity,
  entity,
  reset,
}) => {
  const router = useRouter();

  useEffect(() => {
    if (window && router) {
      router.beforePopState(({ as }) => {
        if (as.includes('/upgrade/')) {
          router.back();
          return false;
        }
        return true;
      });
    }
    return () => router.beforePopState();
  }, []);

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isConfirmationVisible, setIsConfirmationVisible] = useState(false);
  const [isSignInModalOpen, setIsSignInModalOpen] = useState(false);
  const { data: spaSettings } = useGetSpaSettings(property.id);
  const hideArrivalTimes = spaSettings?.hideArrivalTimes || false;

  const { t } = useTranslations();

  const totalGuestAmount = journeyParams.adults + journeyParams.children;
  const isMultiRoom = some(journeyParams.rooms) && journeyParams.rooms.length > 1;
  const addToBasket = isMultiRoom
    ? multiRoomRateAddToBasket({
        basketDetails,
        journeyParams,
        property,
        language,
        entity,
        metaEntity,
      })
    : roomRateAddToBasket({
        basketDetails,
        journeyParams,
        property,
        language,
        entity,
        metaEntity,
      });

  const openModal = () => {
    setIsModalOpen(true);
  };

  const closeModal = () => {
    if (isModalOpen) {
      setIsModalOpen(false);
      destroy(roomRateBookingFormConfig.form);
      destroy(multiRoomRateBookingFormConfig.form);
    }
  };

  useEffect(() => {
    if (
      bookingStep &&
      Object.values(RoomRateFormStepNames).includes(bookingStep as RoomRateFormStepNames)
    ) {
      openModal();
    } else {
      closeModal();
    }
  }, [bookingStep]);

  const showConfirmation = () => {
    setIsConfirmationVisible(true);
  };

  const hideConfirmation = () => {
    router.push({
      pathname: routesMap[metaEntity.type].detail,
      query: {
        id: metaEntity.id,
      },
    });
    setIsConfirmationVisible(false);
  };

  const goToNextStep = async () => {
    if (bookingStep === RoomRateFormStepNames.GUESTS && availableExtras?.length) {
      router.push({
        pathname: routesMap[metaEntity.type].booking,
        query: {
          ...router.query,
          id: metaEntity.id,
          bookingStep: RoomRateFormStepNames.EXTRAS,
          [entity.type]: entity.id,
        },
      });
    } else {
      reset();
      showConfirmation();
      closeModal();
    }
  };

  const goToPreviousStep = () => {
    if (bookingStep === RoomRateFormStepNames.GUESTS) {
      closeModal();
    }
    router.back();
  };

  const roomId = metaEntity.type === 'room' ? metaEntity.id : entity.id;
  const rateId = metaEntity.type === 'room' ? entity.id : metaEntity.id;
  const fromDate = journeyParams.fromDate.format('YYYY-MM-DD');
  const toDate = journeyParams.toDate
    ? journeyParams.toDate.format('YYYY-MM-DD')
    : journeyParams.fromDate.add(1, 'days').format('YYYY-MM-DD');

  const { data: availableExtras } = useRoomExtras({
    ...journeyParams,
    fromDate,
    toDate,
    roomId,
    rateId,
  });

  const formSteps = getFormSteps({
    isSignInModalOpen,
    basketGuests,
    journeyParams,
    addToBasket,
    setIsSignInModalOpen,
    goToNextStep,
    metaEntity,
    entity,
    totalGuestAmount,
    availableExtras,
  });

  const showBasketDrawer = isConfirmationVisible && basketDetails;

  const modules = Object.keys(property.modulesExcludingDisabled);

  const modulesToRecommend = useModulesToRecommend({
    modules: modules as ModuleType[],
    items: basketDetails?.items ?? [],
  });

  return (
    <div>
      {showBasketDrawer && (
        <BasketDrawer
          currencyCode={basketDetails.total.currencyCode}
          numberOfItems={calculateTotalNumberOfItemsInBasket(basketDetails.items)}
          open={isConfirmationVisible}
          onClose={hideConfirmation}
          title={t('basketDrawer.title')}
          totalCost={basketDetails.total.amount}
          modules={modulesToRecommend}
          items={basketDetails?.items}
          hideArrivalTimes={hideArrivalTimes}
        />
      )}

      <Modal isVisible={isModalOpen}>
        <>
          <Spinner showDelayMessage isActive={isFetching} />
          <Header>
            <PrevStepButton onClick={goToPreviousStep} />
            <MobileHeading>{metaEntity.title}</MobileHeading>
            <CloseModalButton
              onClick={() =>
                router.push({
                  pathname: routesMap[metaEntity.type].detail,
                  query: { id: metaEntity.id },
                })
              }
            />
          </Header>
          <InnerScroller scrollToTopIndicator={bookingStep}>
            <FormWrapper>
              <Title titleId={titleIds[bookingStep || RoomRateFormStepNames.GUESTS]} />
              <Section>
                <SubTitle
                  titleId="roomRates.guestsSubtitle"
                  values={{ guestAmount: totalGuestAmount }}
                />
              </Section>
              <SplitBarWrapper>
                <LeftSideBarWrapper>{formSteps[bookingStep || '']}</LeftSideBarWrapper>
                <RightSideBarWrapper>
                  <SignIn />
                </RightSideBarWrapper>
              </SplitBarWrapper>
            </FormWrapper>
          </InnerScroller>
        </>
      </Modal>
      <StickyBarWrapper id={STICKY_BAR_ID} zIndex={isModalOpen ? 9999 : undefined} />
    </div>
  );
};
